import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, withQuery, money, date } from '../api.js';
import DateField from '../components/DateField.jsx';
import Reminder from '../components/Reminder.jsx';
import { usePaymentMethods } from '../hooks.js';
import { useAuth } from '../App.jsx';

/**
 * Длъжници — кой на кого какво дължи.
 *
 * Два раздела, защото това са два различни въпроса: кой ни дължи и на
 * кого дължим. Сумите са с ДДС — това е сумата, която се плаща.
 *
 * Списъкът е по фирми, не по фактури: 363 реда фактури не отговарят на
 * въпроса „кой ми дължи“, а го заравят. Подробностите излизат при
 * щракване върху фирмата.
 *
 * Падежът на пренесените документи е сметнат от уговорените дни отсрочка
 * при контрагента — старата програма не го е записвала по документа.
 * Такъв ред носи звездичка.
 */

const TABS = [
  { key: 'receivable', label: 'Дължат ни', hint: 'неплатени фактури от клиенти' },
  { key: 'payable', label: 'Ние дължим', hint: 'неплатени фактури към доставчици' },
];

/**
 * Кога дългът престава да е забавяне и става стар.
 *
 * Просрочието се мери от падежа и говори за една фактура. Тук въпросът е
 * друг: колко дълго изобщо виси нещо по тази фирма.
 *
 * Три прага, защото са три различни разговора: до месец нищо не се прави,
 * до два месеца се звъни, до половин година се гони, а след това рядко се
 * прибира само.
 */
const STAR_DALG_DNI = 180;
const ZABAVEN_DALG_DNI = 60;
const NABLYUDAVAN_DALG_DNI = 30;

/** На колко дни е датата. */
function daysSince(day) {
  if (!day) return null;
  const then = new Date(`${String(day).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
}

/**
 * По какво може да се подрежда.
 *
 * По азбучен ред по подразбиране: така списъкът е указател — знаеш името
 * и го намираш. Подредбата по сума отговаря на друг въпрос — кой е
 * най-големият длъжник — и се вика нарочно.
 *
 * Числените колони тръгват от голямото надолу, защото там въпросът е кое
 * е най-много; името тръгва от А.
 */
const SORTS = {
  name: { get: (p) => p.name, dir: 1, text: true },
  count: { get: (p) => p.grouped.length, dir: -1 },
  oldest: { get: (p) => p.oldest ?? '9999-99-99', dir: 1, text: true },
  overdue: { get: (p) => p.overdueAmount, dir: -1 },
  method: { get: (p) => p.paymentLabel, dir: 1, text: true },
  remaining: { get: (p) => p.remaining, dir: -1 },
};

/*
 * Филтрите и подредбата живеят в адреса, не в локално състояние.
 *
 * Иначе бутонът „Назад" от картона на контрагента връща на празен списък
 * с нулирани филтри: адресът се пази в историята, но React state — не.
 * С адреса като единствен източник на истина „Назад" връща точно списъка,
 * какъвто е бил — раздел, търсене, подредба — преди да се отвори картонът.
 */
function citiFiltri(params) {
  return {
    side: params.get('side') === 'payable' ? 'payable' : 'receivable',
    overdueOnly: params.get('overdueOnly') === '1',
    search: params.get('q') ?? '',
    sortBy: SORTS[params.get('sortBy')] ? params.get('sortBy') : 'name',
    sortDir: params.get('sortDir') === '-1' ? -1 : 1,
  };
}

/**
 * Забележката на документа и на редовете му (06.10.2026) — при „РАЗНИ
 * ООД?“ само по нея се разбира чий е дългът. Обединена фактура събира
 * забележките на всичките си документи.
 */
function zabelezhka(r) {
  const redove = r.documents ?? [r];
  const vse = [];
  for (const d of redove) {
    for (const t of [d.doc_note, d.line_notes]) {
      const x = String(t ?? '').trim();
      if (x && !vse.includes(x)) vse.push(x);
    }
  }
  return vse.join(' · ');
}

export default function Debtors() {
  const { user } = useAuth();
  const { methods } = usePaymentMethods();
  const methodName = useMemo(() => {
    const byCode = new Map(methods.map((m) => [m.code, m.name]));
    return (code) => byCode.get(code) ?? code;
  }, [methods]);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = citiFiltri(searchParams);
  const side = filters.side;
  const overdueOnly = filters.overdueOnly;
  const search = filters.search;
  const sort = { by: filters.sortBy, dir: filters.sortDir };

  function change(patch) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === '' || v === false || v == null) next.delete(k);
        else next.set(k, String(v));
      }
      return next;
    }, { replace: true });
  }

  const [open, setOpen] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloads, setReloads] = useState(0);
  const [remindPartner, setRemindPartner] = useState(null);

  /*
   * Плик, кликнат до фирмата, маркира се веднага — без презареждане на
   * целия списък, който би затворил отворените редове и би върнал
   * скрола отгоре.
   */
  function markReminded(partnerId) {
    setData((d) => d && ({
      ...d,
      reminded: { ...d.reminded, [partnerId]: new Date().toISOString() },
      remindedCount: {
        ...d.remindedCount, [partnerId]: (d.remindedCount?.[partnerId] ?? 0) + 1,
      },
    }));
  }

  const canWrite = user.role !== 'auditor';
  // Едно и също и в двете посоки: клиент, който ни плаща, и доставчик,
  // на когото плащаме. Разликата е само накъде тръгват парите.
  const payColumn = canWrite;
  const cols = payColumn ? 8 : 7;
  // След записано плащане списъкът се тегли наново — иначе платената
  // фактура още стои като дълг и човек я плаща втори път.
  const reload = () => setReloads((n) => n + 1);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(withQuery('/api/reports/debts', { side, overdueOnly }))
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [side, overdueOnly, reloads]);

  // Смяната на раздела затваря отворената фирма; презареждането — не,
  // защото човек тъкмо е платил и гледа какво остава по нея.
  useEffect(() => { setOpen(null); }, [side, overdueOnly]);

  // Едно задължение на фирма, с фактурите отдолу.
  const partners = useMemo(() => {
    const by = new Map();
    for (const r of data?.rows ?? []) {
      let p = by.get(r.partner_id);
      if (!p) {
        p = {
          id: r.partner_id,
          name: r.partner_name,
          phone: r.phone,
          email: r.email,
          reminded: data?.reminded?.[r.partner_id] ?? null,
          remindedCount: data?.remindedCount?.[r.partner_id] ?? 0,
          rows: [],
          remaining: 0,
          overdue: 0,
          overdueAmount: 0,
          worst: null,
          oldest: null,
          paymentMethods: new Set(),
        };
        by.set(r.partner_id, p);
      }
      p.rows.push(r);
      p.remaining += Number(r.remaining);
      if (r.doc_date && (p.oldest === null || r.doc_date < p.oldest)) p.oldest = r.doc_date;
      // Пренесеното салдо няма документ зад себе си, значи и няма начин на
      // плащане — не бива да превръща „по банка" в „смесено" само защото
      // го има.
      if (r.source === 'document' && r.payment_method) p.paymentMethods.add(r.payment_method);
    }
    // Просрочието и броят се смятат по ГРУПИРАНИТЕ редове (фактура, не
    // продажба зад нея) — иначе горният брояч би показал 209 продажби,
    // докато таблицата отдолу показва 20 фактури.
    for (const p of by.values()) {
      // По дата, най-старото отгоре (07.10.2026) — така и кредитното
      // известие застава на своята дата, а не накрая.
      p.grouped = groupByInvoice(p.rows).sort((a, b) =>
        String(a.doc_date ?? '').localeCompare(String(b.doc_date ?? ''))
        || String(a.doc_number ?? '').localeCompare(String(b.doc_number ?? '')));
      for (const r of p.grouped) {
        if ((r.days_overdue ?? 0) > 0) {
          p.overdue += 1;
          p.overdueAmount += Number(r.remaining);
          if (p.worst === null || r.days_overdue > p.worst) p.worst = r.days_overdue;
        }
      }
      // Едно име, ако целият дълг е дошъл по един и същ начин; иначе
      // „смесено" — та да е сортируемо, не само за показване.
      p.paymentLabel = p.paymentMethods.size === 0 ? '—'
        : p.paymentMethods.size > 1 ? 'смесено'
          : methodName([...p.paymentMethods][0]);
    }
    return [...by.values()];
  }, [data, methodName]);

  const needle = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const spec = SORTS[sort.by] ?? SORTS.name;
    const list = partners.filter((p) => !needle || p.name.toLowerCase().includes(needle));
    return list.sort((a, b) => {
      const x = spec.get(a);
      const y = spec.get(b);
      // Кирилицата не се подрежда вярно с обикновено сравнение на низове.
      const cmp = spec.text ? String(x).localeCompare(String(y), 'bg') : x - y;
      return cmp * sort.dir;
    });
  }, [partners, needle, sort]);

  /** Повторно щракване по същата колона обръща посоката. */
  function sortBy(key) {
    const dir = sort.by === key ? -sort.dir : SORTS[key].dir;
    change({ sortBy: key, sortDir: dir });
  }
  const shown = visible.reduce((s, p) => s + p.remaining, 0);
  const tab = TABS.find((t) => t.key === side);

  // Разбивка на общото задължение по начин на плащане — пренесеното салдо
  // няма начин зад себе си и не влиза тук, затова сборът им може да е
  // по-малък от общата сума горе.
  const methodTotals = useMemo(() => {
    const totals = new Map();
    for (const r of data?.rows ?? []) {
      if (r.source !== 'document' || !r.payment_method) continue;
      totals.set(r.payment_method, (totals.get(r.payment_method) ?? 0) + Number(r.remaining));
    }
    return totals;
  }, [data]);

  // Изнася се това, което се вижда: същият адрес, същите условия.
  const exportUrl = withQuery('/api/reports/debts.xlsx', { side, overdueOnly });

  return (
    <>
      <div className="page-head">
        <h1>Длъжници</h1>
        <div className="spacer" />
        {/* Изнася се това, което се вижда: същият адрес, същите условия. */}
        <a className="button" href={exportUrl} download>Excel</a>
        {canWrite && (
          <Link to="/bank-import"><button>Банково извлечение</button></Link>
        )}
        {data && (
          <>
            <span className="badge">{partners.length} фирми</span>
            <span className={`badge ${side === 'receivable' ? 'ok' : 'danger'}`}>
              {money(data.totals.remaining)} с ДДС
            </span>
            {data.totals.overdue > 0 && (
              <span className="badge danger">просрочени {money(data.totals.overdue)}</span>
            )}
            {methods.filter((m) => (methodTotals.get(m.code) ?? 0) > 0.005).map((m) => (
              <span key={m.code} className="badge" title={`${m.name}: ${money(methodTotals.get(m.code))}`}>
                {m.shortName ?? m.name}: {money(methodTotals.get(m.code))}
              </span>
            ))}
          </>
        )}
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab ${t.key === side ? 'active' : ''}`}
            onClick={() => change({ side: t.key })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          Търсене
          <input
            value={search}
            onChange={(e) => change({ q: e.target.value })}
            placeholder="име на фирма"
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => change({ overdueOnly: e.target.checked })}
          />
          само просрочените
        </label>
        <div className="spacer" />
        {visible.length !== partners.length && (
          <span className="muted">показани {visible.length} фирми — {money(shown)}</span>
        )}
      </div>

      {data && data.totals.fromBalances > 0 && (
        <div className="note">
          {money(data.totals.fromBalances)} от дълга е пренесен като салдо по контрагент и
          няма фактура зад себе си.
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="compact debtors-table">
            <thead>
              <tr>
                <th style={{ width: 20 }} />
                <Head sort={sort} onSort={sortBy} by="name">Фирма</Head>
                <Head sort={sort} onSort={sortBy} by="count" num>Фактури</Head>
                <Head sort={sort} onSort={sortBy} by="oldest">Най-стара</Head>
                <Head sort={sort} onSort={sortBy} by="overdue">Състояние</Head>
                <Head sort={sort} onSort={sortBy} by="method">Начин на плащане</Head>
                <Head sort={sort} onSort={sortBy} by="remaining" num>Задължение</Head>
                {payColumn && <th style={{ width: 260 }}>Плащане</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cols} className="empty">Зареждане…</td></tr>}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={cols} className="empty">
                    Няма {overdueOnly ? 'просрочени ' : ''}задължения — {tab.hint}.
                  </td>
                </tr>
              )}
              {!loading && visible.map((p, i) => (
                <Row
                  key={p.id}
                  partner={p}
                  advance={data?.advances?.[p.id] ?? 0}
                  canPay={payColumn}
                  side={side}
                  cols={cols}
                  methodName={methodName}
                  alt={i % 2 === 1}
                  open={open === p.id}
                  onOpen={() => setOpen(p.id)}
                  onToggle={() => setOpen(open === p.id ? null : p.id)}
                  onPaid={reload}
                  onRemind={setRemindPartner}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {remindPartner && (
        <ReminderModal
          partner={remindPartner}
          onClose={() => setRemindPartner(null)}
          onSent={() => markReminded(remindPartner.id)}
        />
      )}
    </>
  );
}

/**
 * Изпращане на напомняне направо от иконата — без да се отваря редът.
 *
 * Иначе едничкият начин беше да се разгъне фирмата и да се превърти до
 * връзката долу — губи се общата картина на списъка точно когато трябва
 * да се мине през няколко длъжника подред.
 */
function ReminderModal({ partner, onClose, onSent }) {
  const [letter, setLetter] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/reports/debts/reminder/${partner.id}`)
      .then((r) => { if (!cancelled) setLetter(r); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [partner.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/reports/debts/reminder/${partner.id}`, {});
      setDone(res.redirected
        ? `Пробен режим — писмото отиде при нас (${res.to}), не при клиента.`
        : `Изпратено до ${res.to}.`);
      onSent();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
        <div className="card-head">Напомняне до {partner.name}</div>
        <div className="card-body">
          {busy && !letter && !error && <div className="empty">Зареждане…</div>}
          {error && <div className="error">{error}</div>}
          {done && <div className="notice">{done}</div>}

          {letter && !done && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <strong>До:</strong> {letter.to ?? '— няма записан имейл —'}
                <span className="muted">{letter.rows.length} фактури · {money(letter.total)}</span>
                {letter.lastReminder && (
                  <span className="badge">писано на {date(letter.lastReminder.sent_at)}</span>
                )}
              </div>

              <div style={{ marginTop: 6 }}><strong>Тема:</strong> {letter.subject}</div>

              <pre style={{
                whiteSpace: 'pre-wrap', margin: '10px 0', padding: 10,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 12, maxHeight: '40vh', overflowY: 'auto',
              }}
              >
                {letter.text}
              </pre>

              <span className="hint">Към писмото се прикача и таблица с фактурите.</span>

              {!letter.canSend && (
                <div className="muted" style={{ marginTop: 8 }}>
                  {letter.to ? 'пощата не е настроена' : 'няма записан имейл на контрагента'}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          {done || !letter ? (
            <button onClick={onClose}>Затвори</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy}>Откажи</button>
              <button className="primary" disabled={busy || !letter.canSend} onClick={send}>
                Изпрати
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Датата на най-старото задължение, оцветена според възрастта си.
 *
 * Цветът казва „това виси отдавна" с един поглед — иначе датата е просто
 * число и окото я подминава.
 *
 *   зелено  от месец      — гледай го
 *   синьо   от два месеца — обади се
 *   червено от половин година
 */
function oldestCell(day) {
  const age = daysSince(day);
  if (!day || age === null) return '—';
  if (age < NABLYUDAVAN_DALG_DNI) return date(day);

  const color = age >= STAR_DALG_DNI ? 'var(--danger)'
    : age >= ZABAVEN_DALG_DNI ? 'var(--accent)'
      : 'var(--ok)';

  return (
    <span
      style={{ color, fontWeight: 600 }}
      title={`${age} дни от датата на документа`}
    >
      {date(day)}
    </span>
  );
}

/**
 * С какво разполагаме, за да стигнем до тази фирма.
 *
 * Плик, ако има имейл; слушалка, ако има телефон. Смисълът е обратният на
 * обичайния: важното не е че ги има, а че при 39 от 57 длъжника ги НЯМА —
 * и това трябва да се вижда, без да се отваря всеки картон.
 *
 * Пликът е бутон: щраква се направо от списъка, без да се разгъва редът и
 * без да се губи мястото, докъдето е стигнал прегледът. Различен цвят
 * значи, че на този имейл вече е пращано поне едно напомняне.
 *
 * Самите адрес и номер стоят в подсказката, за да се четат, без да
 * задръстват реда.
 */
function Contacts({ partner, onRemind }) {
  const has = (v) => Boolean(String(v ?? '').trim());
  if (!has(partner.email) && !has(partner.phone)) {
    return (
      <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}
        title="няма записан имейл или телефон">
        —
      </span>
    );
  }
  return (
    <span style={{ marginLeft: 6, fontSize: 14, letterSpacing: 3 }}>
      {has(partner.email) && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemind(partner); }}
          title={partner.reminded
            ? `имейл: ${partner.email} · напомняно последно на ${date(partner.reminded)}`
              + (partner.remindedCount > 1 ? ` · общо ${partner.remindedCount} пъти` : '')
              + ' · кликни за ново напомняне'
            : `имейл: ${partner.email} · кликни за напомняне`}
          style={{
            cursor: 'pointer', padding: 0, border: 0, background: 'none', font: 'inherit',
            lineHeight: 1, verticalAlign: 'middle',
            color: partner.reminded ? 'var(--accent)' : 'inherit',
            fontWeight: partner.reminded ? 700 : 400,
          }}>
          {/* U+FE0E след знака иска текстов, едноцветен вариант — иначе
              телефони (най-вече iOS) го рисуват като цветно емоджи и
              никакъв CSS цвят не се вижда, включително този тук. */}
          ✉︎
          {partner.remindedCount > 1 && (
            <sup style={{ fontSize: 9, fontWeight: 700, marginLeft: 1 }}>
              {partner.remindedCount}
            </sup>
          )}
        </button>
      )}
      {has(partner.phone) && (
        <span title={`телефон: ${partner.phone}`} style={{ cursor: 'help' }}>☎︎</span>
      )}
    </span>
  );
}

function Head({ by, sort, onSort, num, children }) {
  const on = sort.by === by;
  return (
    <th
      className={`sortable ${num ? 'num' : ''} ${on ? 'on' : ''}`}
      onClick={() => onSort(by)}
      title="подреди"
    >
      {children}
      <span className="arrow">{on ? (sort.dir > 0 ? '▲' : '▼') : '▲'}</span>
    </th>
  );
}

function Row({
  partner: p, advance, canPay, side, cols, methodName, alt, open, onOpen, onToggle, onPaid, onRemind,
}) {
  const payable = side === 'payable';
  // Избраните фактури живеят, докато фирмата е отворена. Затварянето ги
  // забравя — иначе човек се връща след час и плаща стар избор.
  const [picked, setPicked] = useState(() => new Set());
  const [paying, setPaying] = useState(false);
  const [whole, setWhole] = useState(false);
  const [typed, setTyped] = useState('');
  const [hint, setHint] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) { setPicked(new Set()); setPaying(false); setHint(null); }
  }, [open]);

  /**
   * Плащането тръгва оттук, по един от двата пътя.
   *
   * Отметката значи „платил е всичко" — маркира се целият дълг и
   * толкова. Сумата значи „платил е това" — програмата търси кои
   * неплатени фактури правят точно нея, с предимство на най-старите, и
   * ги отбелязва. Не се ли получава точен сбор, не гадае: казва го и
   * оставя избора на човека.
   */
  async function start() {
    onOpen();
    setHint(null);

    if (whole) {
      setPicked(new Set(p.rows.map(key)));
      setPaying(true);
      return;
    }

    const amount = round2(Number(String(typed).replace(',', '.')) || 0);
    if (amount <= 0) return;

    setSearching(true);
    try {
      const res = await api.get(withQuery('/api/reports/debts/match', {
        partnerId: p.id, amount, side,
      }));
      if (res.found) {
        setPicked(new Set(res.documentIds.map((id) => `d${id}`)));
        const n = res.documentIds.length;
        setHint({
          // Сборът е точен, но при много фактури същата сума се получава
          // и по друг начин — тогава отбелязаното е предположение, не
          // отговор, и това си личи по цвета.
          kind: res.unique === false ? 'warn' : 'ok',
          text: `${n} ${n === 1 ? 'фактура прави' : 'фактури правят'} точно ${money(amount)}.`
            + (res.unique === false
              ? ' Същата сума обаче се получава и по друг начин — тук са най-старите.'
                + ' Сверù номерата с преводното, преди да потвърдиш.'
              : ' Прегледай отбелязаното и потвърди.'),
        });
        setPaying(true);
      } else {
        setPicked(new Set());
        setPaying(false);
        setHint({
          kind: 'warn',
          text: `Няма фактури, които да правят точно ${money(amount)}`
            + `${res.reason ? ` (${res.reason})` : ''}. Отбележи ги сам.`,
        });
      }
    } catch (err) {
      setHint({ kind: 'warn', text: err.message });
    } finally {
      setSearching(false);
    }
  }

  // Само редовете с документ могат да поемат разпределение; пренесеното
  // салдо няма фактура зад себе си.
  const withDocs = p.rows.filter((r) => r.source === 'document');
  const chosen = p.rows.filter((r) => picked.has(key(r)));
  const chosenSum = round2(chosen.reduce((s, r) => s + Number(r.remaining), 0));

  // Показваната таблица е групирана по фактура (сметнато веднъж по-горе, в
  // общия списък); отметките и разпределението отдолу продължават да
  // работят по истинските документи (виж key/rowDocKeys) — отметка на
  // групиран ред просто отбелязва всичките ѝ документи наведнъж.
  const grouped = p.grouped;

  function isGroupPicked(r) {
    return rowDocKeys(r).every((k) => picked.has(k));
  }
  function isGroupPartial(r) {
    const ks = rowDocKeys(r);
    return !ks.every((k) => picked.has(k)) && ks.some((k) => picked.has(k));
  }
  function toggleRow(r) {
    const ks = rowDocKeys(r);
    setPicked((s) => {
      const next = new Set(s);
      const allIn = ks.every((k) => next.has(k));
      if (allIn) ks.forEach((k) => next.delete(k));
      else ks.forEach((k) => next.add(k));
      return next;
    });
  }

  const allPicked = p.rows.length > 0 && picked.size === p.rows.length;

  return (
    <>
      <tr className={`clickable ${open ? 'open' : ''} ${alt ? 'row-alt' : ''}`} onClick={onToggle}>
        <td className="muted">{open ? '▾' : '▸'}</td>
        <td>
          <b>{p.name}</b>
          <Contacts partner={p} onRemind={onRemind} />
          {advance > 0.005 && (
            <span className="badge ok" style={{ marginLeft: 6 }}
              title="платено, което още не стои по фактура">
              аванс {money(advance)}
            </span>
          )}
        </td>
        <td className="num">{p.grouped.length}</td>
        <td>{oldestCell(p.oldest)}</td>
        <td>
          {p.overdue > 0
            ? <span className="badge danger">
                {p.overdue} просрочени · {money(p.overdueAmount)}
              </span>
            : <span className="muted">в срок</span>}
        </td>
        <td className={p.paymentLabel === 'смесено' ? 'muted' : undefined}>{p.paymentLabel}</td>
        <td className="num"><b>{money(p.remaining)}</b></td>

        {/* Двата пътя стоят до самото задължение, защото окото е там:
            отметка „платил е всичко" и поле „платил е толкова". */}
        {canPay && (
          <td onClick={(e) => e.stopPropagation()}>
            <div className="pay-cell">
              <label className="check" title="плаща цялото задължение">
                <input type="checkbox" checked={whole}
                  onChange={(e) => { setWhole(e.target.checked); if (e.target.checked) setTyped(''); }} />
                цялото
              </label>
              <input
                type="number" step="0.01" min="0" placeholder="сума"
                value={typed}
                disabled={whole}
                onChange={(e) => { setTyped(e.target.value); setHint(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') start(); }}
                style={{ width: 92 }}
              />
              <button
                className="primary"
                disabled={searching || (!whole && !(Number(String(typed).replace(',', '.')) > 0))}
                onClick={start}
              >
                {searching ? '…' : 'Плащане'}
              </button>
            </div>
          </td>
        )}
      </tr>

      {open && (
        <tr className="detail">
          <td />
          <td colSpan={cols - 1}>
            {hint && (
              <div className={hint.kind === 'ok' ? 'note' : 'warning'}
                style={{ marginBottom: 8 }}>
                {hint.text}
              </div>
            )}
            <div className="detail-head">
              <Link to={`/partners/${p.id}`}>Отвори контрагента</Link>
              {p.phone && <span className="muted">тел. {p.phone}</span>}
              {/* Лист само за тази фирма — за изпращане до самата нея. */}
              <a
                className="link"
                href={withQuery('/api/reports/debts.xlsx', { side, partnerId: p.id })}
                download
              >
                Excel за {side === 'receivable' ? 'клиента' : 'доставчика'}
              </a>
              {/* Напомняне се праща само на клиенти — на доставчик се плаща. */}
              {side === 'receivable' && canPay && (
                <Reminder partnerId={p.id} onSent={onPaid} />
              )}
            </div>
            <table className="inner">
              <thead>
                <tr>
                  {canPay && (
                    <th style={{ width: 24 }}>
                      <input
                        type="checkbox"
                        checked={allPicked}
                        title="цялото задължение"
                        onChange={(e) => setPicked(e.target.checked
                          ? new Set(p.rows.map(key)) : new Set())}
                      />
                    </th>
                  )}
                  {/* При доставчиците вътрешният номер не трябва (29.10.2026) — фактурата води към документа. */}
                  {!payable && <th>Документ</th>}
                  <th>Дата</th>
                  <th>Фактура №</th>
                  <th>Падеж</th>
                  <th className="num">Сума</th>
                  <th className="num">Платено</th>
                  <th className="num">Остава</th>
                  <th>Начин</th>
                  <th>Състояние</th>
                  <th>Забележка</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((r, i) => (
                  <tr key={r.invoice_no ? `inv${r.invoice_no}` : (r.document_id ?? `b${i}`)}
                    className={isGroupPicked(r) ? 'picked-row' : undefined}>
                    {canPay && (
                      <td>
                        <input
                          type="checkbox"
                          checked={isGroupPicked(r)}
                          ref={(el) => { if (el) el.indeterminate = isGroupPartial(r); }}
                          onChange={() => toggleRow(r)}
                        />
                      </td>
                    )}
                    {!payable && (
                    <td>
                      {r.documents
                        ? <Link to={`/print/invoice/${r.invoice_no}`}>
                            {r.documents.length} документа
                          </Link>
                        : (r.document_id
                          ? <Link to={`/documents/${r.document_id}`}>{r.doc_number}</Link>
                          : <span className="muted">салдо при пренасянето</span>)}
                    </td>
                    )}
                    <td>{r.doc_date ? date(r.doc_date) : '—'}</td>
                    <td>
                      {payable && r.document_id
                        ? <Link to={`/documents/${r.document_id}`} title={`документ № ${r.doc_number}`}>{r.invoice_no ?? r.doc_number}</Link>
                        : (r.invoice_no ?? (payable && !r.document_id ? <span className="muted">салдо при пренасянето</span> : '—'))}
                    </td>
                    <td>
                      {r.due_date ? date(r.due_date) : '—'}
                      {r.due_is_derived ? (
                        <span
                          className="muted"
                          title={`Падежът не е записан по фактурата — сметнат е от ${r.credit_days || 0} дни отсрочка при контрагента`}
                        > *</span>
                      ) : null}
                    </td>
                    <td className="num">{r.source !== 'balance' ? money(r.total) : '—'}</td>
                    <td className="num">
                      {r.source === 'document' && Number(r.paid_amount) > 0
                        ? money(r.paid_amount) : '—'}
                      {r.is_carry_boundary ? (
                        <span
                          className="muted"
                          title={'Оттук започва пренесеният дълг. Салдото от старата '
                            + 'програма се изчерпва вътре в тази фактура — остатъкът ѝ '
                            + 'не влиза в задължението.'}
                        > ⓘ</span>
                      ) : null}
                    </td>
                    <td className="num"><b>{money(r.remaining)}</b></td>
                    <td>
                      {r.source === 'document'
                        ? (r.payment_method ? methodName(r.payment_method)
                          : (r.documents ? 'различни' : '—'))
                        : '—'}
                    </td>
                    <td><State row={r} /></td>
                    <td className="muted" style={{ fontSize: 12, maxWidth: 360 }}>
                      {zabelezhka(r) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {p.rows.some((r) => r.is_carry_boundary) && (
              <div className="detail-foot">
                ⓘ Дългът от старата програма свършва по средата на най-старата фактура
                тук — платената ѝ част не е плащане по нея, а границата, докъдето стига
                салдото.
              </div>
            )}

            {/* Сумата на избраното стои до копчето — човекът сверява нея
                с това, което е дошло по банката, преди да потвърди. */}
            {canPay && (
              <div className="detail-foot" style={{
                display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
              }}>
                {picked.size === 0 ? (
                  <span className="muted">
                    Отбележи фактурите, които клиентът е платил — или горната отметка за
                    цялото задължение.
                  </span>
                ) : (
                  <>
                    <span>
                      избрани <b>{picked.size}</b> от {p.rows.length} · сума{' '}
                      <b>{money(chosenSum)}</b>
                    </span>
                    {!paying && (
                      <button className="primary" onClick={() => setPaying(true)}>
                        Плащане
                      </button>
                    )}
                    <button className="link" onClick={() => setPicked(new Set())}>
                      изчисти избора
                    </button>
                  </>
                )}
              </div>
            )}

            {paying && (
              <SettleForm
                partner={p}
                chosen={chosen}
                chosenSum={chosenSum}
                /* Написаната сума пътува дотук: не се ли е получил
                   точен сбор, човекът избира сам, но платеното си е
                   каквото е дошло, не сборът на избраното. */
                suggested={round2(Number(String(typed).replace(',', '.')) || 0)}
                payable={payable}
                others={withDocs.filter((r) => !picked.has(key(r)))}
                onClose={() => setPaying(false)}
                onDone={() => {
                  setPicked(new Set());
                  setPaying(false);
                  setWhole(false);
                  setTyped('');
                  setHint(null);
                  onPaid();
                }}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Ключ на ред: документите имат номер, пренесеното салдо — не. */
const key = (r) => (r.document_id ? `d${r.document_id}` : 'balance');

const round2 = (n) => Math.round(n * 100) / 100;

/** По-ранната от две дати във вид "YYYY-MM-DD" (или единствената налична). */
function earlier(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return String(a) < String(b) ? a : b;
}

/**
 * Групира редовете на един контрагент по фактура.
 *
 * „Фактуриране" (виж Invoicing.jsx) обединява няколко продажби под един
 * `invoice_no` — до днешната промяна те се показваха тук като толкова
 * отделни реда, колкото продажби има зад фактурата.
 *
 * Показаната „Дата" е датата на самата фактура (`invoice_date`), не датата
 * на продажбата зад нея — двете съвсем не съвпадат, когато фактурата
 * обединява доставки от цял период (напр. болница, фактурирана в края на
 * месеца за всички доставки през месеца). Падежът и начинът на плащане
 * идват от НАЙ-РАННИЯ документ в групата, както при преглед на фактура в
 * `invoices.js`.
 *
 * Продажби без фактура и пренесеното салдо си остават единични редове.
 */
function groupByInvoice(rows) {
  const order = [];
  const byInvoice = new Map();
  for (const r of rows) {
    if (!r.invoice_no) { order.push({ invoiceNo: null, docs: [r] }); continue; }
    let g = byInvoice.get(r.invoice_no);
    if (!g) {
      g = { invoiceNo: r.invoice_no, docs: [] };
      byInvoice.set(r.invoice_no, g);
      order.push(g);
    }
    g.docs.push(r);
  }

  return order.map((g) => {
    const grouping = g.docs.length > 1;
    const docs = [...g.docs].sort((a, b) => String(a.doc_date).localeCompare(String(b.doc_date))
      || (a.document_id ?? 0) - (b.document_id ?? 0));
    const [first] = docs;
    const invoiceDate = docs.reduce((min, d) => earlier(min, d.invoice_date), null);
    if (!invoiceDate) return grouping ? { ...first, documents: docs } : first;

    /*
     * Падежът на ФАКТУРАТА, не на продажбата зад нея — датата на
     * продажбата може да е с недели по-стара от реалното издаване, когато
     * фактурата обединява доставки от цял период (напр. болница,
     * фактурирана в края на месеца за доставките през месеца). Ръчно
     * зададен падеж по някой от документите бие всичко.
     */
    const withExplicit = docs.find((d) => !d.due_is_derived);
    const dueDate = withExplicit ? withExplicit.due_date : first.due_date_from_invoice;
    const daysOverdue = withExplicit ? withExplicit.days_overdue : first.days_overdue_from_invoice;

    if (!grouping) {
      return {
        ...first, doc_date: invoiceDate, due_date: dueDate, days_overdue: daysOverdue,
        due_is_derived: !withExplicit,
      };
    }
    const methods = new Set(docs.map((d) => d.payment_method));
    return {
      ...first,
      documents: docs,
      document_id: null,
      doc_date: invoiceDate,
      due_date: dueDate,
      days_overdue: daysOverdue,
      due_is_derived: !withExplicit,
      total: round2(docs.reduce((s, d) => s + Number(d.total), 0)),
      paid_amount: round2(docs.reduce((s, d) => s + Number(d.paid_amount), 0)),
      remaining: round2(docs.reduce((s, d) => s + Number(d.remaining), 0)),
      payment_method: methods.size === 1 ? first.payment_method : null,
      is_carry_boundary: docs.some((d) => d.is_carry_boundary),
    };
  });
}

/** Ключовете на всички документи зад един (евентуално групиран) ред. */
const rowDocKeys = (r) => (r.documents ?? [r]).map(key);

/**
 * Плащане по избраните фактури.
 *
 * Сумата тръгва равна на избраното, защото такъв е обичайният случай —
 * клиентът плаща каквото дължи. Но се пипа: по банка хората бъркат и
 * превеждат по-малко или повече, а програмата трябва да отрази
 * действително платеното, не желаното.
 *
 * Разпределението върви от най-старата фактура надолу. Стигнат ли парите
 * дотам, всяка се закрива напълно; последната остава частично платена и
 * екранът го казва изрично, с име и сума.
 */
function SettleForm({ partner, chosen, chosenSum, suggested, payable, others, onClose, onDone }) {
  const { methods } = usePaymentMethods();
  const [amount, setAmount] = useState(
    String((suggested > 0 ? suggested : chosenSum).toFixed(2))
  );
  const [payDate, setPayDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [registers, setRegisters] = useState([]);
  const [registerId, setRegisterId] = useState('');
  const [spread, setSpread] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/cash-registers')
      .then((rows) => { setRegisters(rows); setRegisterId(String(rows[0]?.id ?? '')); })
      .catch(() => setRegisters([]));
  }, []);

  // За плащане на стар дълг стават само начините, с които наистина
  // влизат пари. „Подарък" и „в брой на разсрочено" са състояния на
  // продажбата, не начин да се плати по нея.
  const usable = methods.filter((m) => !['gift', 'cash_deferred'].includes(m.code));

  /*
   * Начинът на плащане не се избира на ръка — той е записан на самата
   * продажба, и плащането трябва да го спазва буквално. Иначе фискалната
   * наличност излиза грешна: продажба по банка, отбелязана после като
   * платена в брой, слага в чекмеджето пари, които никой не е броил —
   * а обратното вдига фиктивен излишък. Виж paymentMethodOf/
   * serviceOperationFor.
   *
   * „В брой на разсрочено" се урежда като обикновено „в брой" — плаща се
   * в брой, макар продажбата да е записана разсрочено. Пренесеното салдо
   * няма документ зад себе си и не носи начин — там изборът си остава.
   */
  const docRows = chosen.filter((r) => r.source === 'document' && r.payment_method);
  const requiredMethods = [...new Set(
    docRows.map((r) => (r.payment_method === 'cash_deferred' ? 'cash' : r.payment_method))
  )];
  const lockedMethod = requiredMethods.length === 1 ? requiredMethods[0] : null;
  const mixedMethods = requiredMethods.length > 1;

  const [freeMethod, setFreeMethod] = useState('bank');
  const method = lockedMethod ?? freeMethod;
  const chosenMethod = usable.find((m) => m.code === method);
  const needsRegister = !!chosenMethod?.affectsCashBalance;

  const paid = round2(Number(String(amount).replace(',', '.')) || 0);
  const diff = round2(paid - chosenSum);
  // Излишъкът се разнася само по фактури със същия начин на плащане —
  // иначе плащане, заключено като „в брой", би затворило и фактура „по
  // банка", която никакви пари не са стигнали в брой.
  const eligibleOthers = lockedMethod
    ? others.filter((r) => (r.payment_method === 'cash_deferred' ? 'cash' : r.payment_method) === lockedMethod)
    : others;
  const otherSum = round2(eligibleOthers.reduce((s, r) => s + Number(r.remaining), 0));

  /*
   * Разпределението, така както ще замине към сървъра.
   *
   * Сумите са във валутата на документа, не в евро: до 2025 г. има
   * фактури в лева и „платено" по тях се води в лева. Затова покритата
   * докрай фактура получава точно остатъка си в собствената си валута —
   * така се затваря без трохи от закръгляне.
   */
  // Кредитните известия (с минус) покриват първо избраните фактури —
  // плащането е само за разликата (07.10.2026).
  const kreditni = chosen.filter((r) => r.source === 'credit');
  const kreditSum = round2(-kreditni.reduce((s, r) => s + Number(r.remaining), 0));

  const plan = useMemo(() => {
    const docs = [...chosen]
      .filter((r) => r.document_id && r.source !== 'credit')
      .sort((a, b) => String(a.doc_date).localeCompare(String(b.doc_date)));

    const out = [];
    let leftEur = round2(paid + kreditSum);

    const fill = (list) => {
      for (const r of list) {
        if (leftEur <= 0.004) break;
        // Курсът на документа, изведен от самия ред: остатъкът в евро
        // върху остатъка в неговата валута. При евровите е 1.
        const own = round2(Number(r.total) - Number(r.paid_amount));
        const remainingEur = Number(r.remaining);
        const rate = own > 0 ? remainingEur / own : 1;
        if (leftEur + 0.004 >= remainingEur) {
          out.push({
            documentId: r.document_id, amount: own, eur: remainingEur, row: r, full: true,
          });
          leftEur = round2(leftEur - remainingEur);
        } else {
          out.push({
            documentId: r.document_id,
            amount: round2(rate > 0 ? leftEur / rate : leftEur),
            eur: leftEur,
            row: r,
            full: false,
          });
          leftEur = 0;
        }
      }
    };

    fill(docs);
    const spilled = [];
    if (leftEur > 0.004 && spread) {
      const before = out.length;
      fill([...eligibleOthers].sort((a, b) => String(a.doc_date).localeCompare(String(b.doc_date))));
      spilled.push(...out.slice(before));
    }
    return { allocations: out, leftover: round2(leftEur), spilled };
  }, [chosen, eligibleOthers, paid, spread, kreditSum]);

  const unpaidRow = plan.allocations.find((a) => !a.full);
  const untouched = chosen.filter((r) => r.document_id && r.source !== 'credit'
    && !plan.allocations.some((a) => a.documentId === r.document_id));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/payments', {
        // Към доставчик парите излизат — оттам и служебно изведената
        // сума към фискалното устройство, ако плащаме в брой.
        direction: payable ? 'out' : 'in',
        paymentDate: payDate,
        partnerId: partner.id,
        method,
        cashRegisterId: needsRegister ? Number(registerId) || null : null,
        amount: paid,
        currency: 'EUR',
        note: note.trim() || null,
        allocations: plan.allocations.map((a) => ({
          documentId: a.documentId, amount: a.amount,
        })),
        credits: kreditni.map((r) => ({ documentId: r.document_id })),
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settle">
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <label className="field">
          {payable ? 'Платена от нас сума, €' : 'Платена сума, €'}
          <input type="number" step="0.01" min="0" value={amount}
            style={{ width: 130, fontWeight: 600 }}
            onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="field">
          Начин
          {lockedMethod ? (
            <input value={chosenMethod?.name ?? lockedMethod} disabled
              title="Идва от продажбата — не се сменя на ръка." />
          ) : (
            <select value={freeMethod} onChange={(e) => setFreeMethod(e.target.value)}>
              {usable.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          )}
        </label>
        {needsRegister && (
          <label className="field">
            Каса
            <select value={registerId} onChange={(e) => setRegisterId(e.target.value)}>
              {registers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <label className="field">
          Дата
          <DateField value={payDate} onChange={setPayDate} />
        </label>
        <label className="field" style={{ flex: 1, minWidth: 160 }}>
          Бележка
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="например номер на преводното" />
        </label>
      </div>

      <div className="settle-check">
        <span>избрано: <b>{money(chosenSum)}</b></span>
        <span>{payable ? 'платихме' : 'платено'}: <b>{money(paid)}</b></span>
        {Math.abs(diff) < 0.005
          ? <span className="badge ok">сумите съвпадат</span>
          : diff < 0
            ? <span className="badge warn">недостигат {money(-diff)}</span>
            : <span className="badge warn">повече с {money(diff)}</span>}
      </div>

      {unpaidRow && (
        <div className="note" style={{ marginTop: 8 }}>
          Фактура <b>{unpaidRow.row.invoice_no ?? unpaidRow.row.doc_number}</b> остава
          частично платена — по нея ще останат{' '}
          <b>{money(round2(Number(unpaidRow.row.remaining) - unpaidRow.eur))}</b>.
        </div>
      )}
      {untouched.length > 0 && (
        <div className="note" style={{ marginTop: 8 }}>
          Парите не стигат за {untouched.length}{' '}
          {untouched.length === 1 ? 'избрана фактура' : 'избрани фактури'} — остават
          изцяло неплатени: {untouched.map((r) => r.invoice_no ?? r.doc_number).join(', ')}.
        </div>
      )}

      {(diff > 0.005 || plan.leftover > 0.004) && (
        <div className="note" style={{ marginTop: 8 }}>
          {eligibleOthers.length > 0 && (
            <label className="check" style={{ marginRight: 10 }}>
              <input type="checkbox" checked={spread}
                onChange={(e) => setSpread(e.target.checked)} />
              разнеси остатъка по другите неплатени със същия начин
              ({eligibleOthers.length} за {money(otherSum)})
            </label>
          )}
          {plan.spilled.length > 0 && (
            <> Ще покрие още:{' '}
              {plan.spilled.map((a) => a.row.invoice_no ?? a.row.doc_number).join(', ')}.
            </>
          )}
          {plan.leftover > 0.004 && (
            <> <b>{money(plan.leftover)}</b> остават като аванс по сметката
              {payable ? ' на доставчика и намаляват дълга ни към него.'
                       : ' на клиента и намаляват задължението му.'}</>
          )}
        </div>
      )}

      {/* Пренесеното салдо няма фактура зад себе си, значи и няма къде
          да се закачи плащането. Отива като аванс, а той се вади от
          задължението — сметката излиза, но си заслужава да се каже. */}
      {chosen.some((r) => !r.document_id) && (
        <div className="note" style={{ marginTop: 8 }}>
          Пренесеното салдо няма фактура — платеното по него се записва като аванс по
          сметката на клиента и намалява задължението му с толкова.
        </div>
      )}

      {mixedMethods && (
        <div className="warning" style={{ marginTop: 8 }}>
          Избраните фактури изискват различни начини на плащане ({requiredMethods
            .map((code) => usable.find((m) => m.code === code)?.name ?? code).join(', ')}) —
          не могат да се запишат с едно плащане. Отбележи ги на отделни групи.
        </div>
      )}

      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

      <div className="toolbar" style={{ marginTop: 8, marginBottom: 0 }}>
        <button onClick={onClose}>Откажи</button>
        <button className="primary" onClick={submit} disabled={busy || paid < 0 || (paid === 0 && kreditni.length === 0) || mixedMethods}>
          {busy ? 'Записва…' : `Запиши плащане ${money(paid)}`}
        </button>
      </div>
    </div>
  );
}

/** Просрочен ли е и с колко. Без падеж не се съди — може и да не е закъснял. */
function State({ row }) {
  if (row.source === 'balance') return <span className="muted">пренесено салдо</span>;
  if (row.source === 'credit') return <span className="badge ok">кредитно известие — за приспадане</span>;
  const days = row.days_overdue;
  if (days === null || days === undefined) return <span className="muted">без падеж</span>;
  if (days > 365) {
    return <span className="badge danger">просрочен с {Math.floor(days / 30)} месеца</span>;
  }
  if (days > 0) return <span className="badge danger">просрочен с {days} дни</span>;
  if (days === 0) return <span className="badge warn">днес е падежът</span>;
  return <span className="muted">още {-days} дни</span>;
}
